import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import { Assignment, Add, Edit, Delete, AddComment, Settings } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { TASKS_LIST_QUERY, DELETE_TASK, TYPE_OF_TASKS_QUERY, PEOPLE_QUERY } from '../../graphql/tasks';
import { PROJECT_TREE_QUERY, ProjectsListQuery, GET_COMPANIES } from '../../graphql/projects';
import { BOARDS_LIST_QUERY } from '../../graphql/boards';
import LoadingOverlay from '../common/LoadingOverlay';
import TasksFilters from './TasksFilters';
import TaskFormModal from './TaskFormModal';
import TaskViewModal from './TaskViewModal';
import TaskProgressModal from './TaskProgressModal';

const TasksList = () => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskViewModalOpen, setTaskViewModalOpen] = useState(false);
  const [taskProgressModalOpen, setTaskProgressModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  // Initialize filters from URL
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      name: params.get('name') || '',
      project: params.get('project') || '',
      element: params.get('element') || '',
      company: params.get('company') || '',
      types: params.get('types') || '',
      priority: params.get('priority') || '',
      complete: params.get('complete') || '',
      responsible: params.get('responsible') || '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || ''
    };
  };

  const [filters, setFilters] = useState(getFiltersFromURL);
  const [currentURL, setCurrentURL] = useState(window.location.search);

  // Initialize pagination and sorting from URL
  const params = new URLSearchParams(window.location.search);
  const [page, setPage] = useState(parseInt(params.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(params.get('rowsPerPage')) || 25);
  const [sortColumn, setSortColumn] = useState(params.get('sortColumn') || 'id');
  const [sortDirection, setSortDirection] = useState(params.get('sortDirection') || 'ASC');



  const { data: typeOfTasksData, loading: typeOfTasksLoading, refetch: refetchTypes } = useQuery(TYPE_OF_TASKS_QUERY, {
    fetchPolicy: 'network-only'
  });

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { page: 1, size: 10000 },
    fetchPolicy: 'cache-first'
  });

  const { data: projectsData } = useQuery(ProjectsListQuery, {
    variables: { pageSize: 1000, page: 1 },
    fetchPolicy: 'cache-first'
  });

  const { data: companiesData } = useQuery(GET_COMPANIES, {
    fetchPolicy: 'cache-first'
  });

  // Get all projects tree for mapping project names
  const { data: allProjectsTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: 1 },
    fetchPolicy: 'cache-first'
  });

  const { data: boardsData } = useQuery(BOARDS_LIST_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data, loading, refetch } = useQuery(TASKS_LIST_QUERY, {
    variables: { 
      page: page + 1, 
      size: rowsPerPage,
      sort_column: sortColumn,
      sort_direction: sortDirection,
      filters: {
        project_id: filters.project ? parseInt(filters.project) : null,
        element_id: filters.element ? parseInt(filters.element) : null,
        company_ids: filters.company ? [parseInt(filters.company)] : null,
        priority: filters.priority ? parseInt(filters.priority) : null,
        name: filters.name || null,
        task_types: filters.types ? [filters.types.toString()] : null,
        responsible_ids: filters.responsible ? [parseInt(filters.responsible)] : null,
        complete: filters.complete ? (
          filters.complete === 'New' ? 0 :
          filters.complete === 'Done' ? 100 :
          filters.complete === 'In progress' ? -1 : null
        ) : null,
        start_date_from: filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00.000Z').toISOString() : null,
        start_date_to: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59.999Z').toISOString() : null
      }
    },
    fetchPolicy: 'cache-and-network',
    skip: typeOfTasksLoading
  });

  // Get project tree for selected filter project
  const { data: projectTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: filters.project },
    fetchPolicy: 'cache-first',
    skip: !filters.project
  });

  // Flatten all projects tree into lookup for project names
  const allProjects = useMemo(() => {
    const projects = {};
    if (allProjectsTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(allProjectsTreeData.project_tree);
        const flattenTree = (items) => {
          items.forEach(item => {
            projects[item.id] = item;
            if (item.children) flattenTree(item.children);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing all projects tree:', error);
      }
    }
    return projects;
  }, [allProjectsTreeData]);

  // Flatten project tree into lookup for elements
  const allElements = useMemo(() => {
    const elements = {};
    if (projectTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(projectTreeData.project_tree);
        const flattenTree = (items, depth = 0) => {
          items.forEach(item => {
            elements[item.id] = { ...item, depth };
            if (item.children) flattenTree(item.children, depth + 1);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing project tree:', error);
      }
    }
    return elements;
  }, [projectTreeData]);
  const [deleteTask] = useMutation(DELETE_TASK);



  const handleChangePage = (event, newPage) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());
    navigate(`/tasks?${params.toString()}`);
  };

  const handleChangeRowsPerPage = (event) => {
    const params = new URLSearchParams(window.location.search);
    params.set('rowsPerPage', event.target.value);
    params.set('page', '0');
    navigate(`/tasks?${params.toString()}`);
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'ASC';
    const newDirection = isAsc ? 'DESC' : 'ASC';
    
    const params = new URLSearchParams(window.location.search);
    params.set('sortColumn', column);
    params.set('sortDirection', newDirection);
    navigate(`/tasks?${params.toString()}`);
  };

  // Update state when URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPage(parseInt(params.get('page')) || 0);
    setRowsPerPage(parseInt(params.get('rowsPerPage')) || 25);
    setSortColumn(params.get('sortColumn') || 'id');
    setSortDirection(params.get('sortDirection') || 'ASC');
  }, [window.location.search]);

  // Reset page and refetch when filters change
  useEffect(() => {
    if (window.location.search !== currentURL) {
      setPage(0);
      setCurrentURL(window.location.search);
      refetch();
    }
  }, [filters, currentURL, refetch]);

  const getPriorityLabel = (priority) => {
    const labels = ['Highest', 'High', 'Normal', 'Low', 'Lowest'];
    return labels[priority] || 'Normal';
  };

  const getPriorityColor = (priority) => {
    const colors = ['error', 'warning', 'info', 'success', 'default'];
    return colors[priority] || 'info';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;

    try {
      await deleteTask({ variables: { id: taskToDelete.id.toString() } });
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      refetch();
      refetchTypes();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  if (loading || typeOfTasksLoading) return <LoadingOverlay />;

  const tasks = data?.tasks?.list?.filter(task => task !== null) || [];
  
  const metadata = data?.tasks?.metadata || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assignment sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Tasks
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<Add />}
          onClick={() => {
            setSelectedTask(null);
            setTaskModalOpen(true);
          }}
        >
          Add Task
        </Button>
      </Box>
      <TasksFilters
        filters={filters}
        setFilters={setFilters}
      />
      <TableContainer component={Paper}>
        <Table
          stickyHeader
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'fixed'
          }}
        >
            <TableHead>
              <TableRow>
                <TableCell sx={{ maxWidth: 0 }}>
                <TableSortLabel
                active={sortColumn === 'name'}
                direction={sortColumn === 'name' ? sortDirection.toLowerCase() : 'asc'}
                onClick={() => handleSort('name')}
                >
                Name
                </TableSortLabel>
                </TableCell>
                <TableCell sx={{ maxWidth: 0 }}>
                Project / Element
                </TableCell>
                <TableCell sx={{ maxWidth: 0 }}>
                Company
                </TableCell>
                <TableCell sx={{ maxWidth: 0 }}>
                Responsible
                </TableCell>
                <TableCell sx={{ maxWidth: 0 }}>
                Types
                </TableCell>
                <TableCell sx={{ width: 110 }}>
                Priority
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                Dates
                </TableCell>
                <TableCell sx={{ width: 90 }}>
                Complete
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: 60,
                    minWidth: 60,
                    maxWidth: 60,
                    px: 1
                  }}
                >
                  <Settings
                    fontSize="small"
                    sx={{
                      color: 'text.secondary'
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {tasks.map((task, index) => (
              <TableRow
              key={task.id}
              hover
              onClick={() => {
              setSelectedTask(task)
              setTaskViewModalOpen(true)
              }}
              sx={{
              cursor: 'pointer',
              '&:nth-of-type(even)': {
              backgroundColor: 'action.hover'
              }
              }}
              >
              {/* NAME */}
              <TableCell
                sx={{
                  width: 260,
                  maxWidth: 260,
                  overflow: 'hidden'
                }}
              >
                <Tooltip title={task.name} arrow>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      display: 'block'
                    }}
                  >
                    {task.name}
                  </Typography>
                </Tooltip>
                {task.description && (
                  <Tooltip title={task.description} arrow>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',

                        // 🔥 KLUCZOWE — TO MUSI BYĆ NORMAL
                        whiteSpace: 'nowrap',

                        width: '100%',
                        display: 'block'
                      }}
                    >
                      {task.description}
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>
              {/* PROJECT */}
              <TableCell sx={{ maxWidth: 0 }}>
                <Tooltip title={task.project?.name || '-'} arrow>
                <Typography noWrap>
              {task.project?.name || '-'}
                </Typography>
                </Tooltip>
                <Tooltip title={task.element?.name || '-'} arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
              {task.element?.name || '-'}
              </Typography>
              </Tooltip>
              </TableCell>
              {/* COMPANY */}
              <TableCell sx={{ maxWidth: 0 }}>
              <Tooltip title={task.companies?.[0]?.name || '-'} arrow>
              <Typography noWrap>
              {task.companies?.[0]?.name || '-'}
              </Typography>
              </Tooltip>
              </TableCell>
              {/* RESPONSIBLE */}
              <TableCell sx={{ maxWidth: 0 }}>
              <Box
              sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              overflow: 'hidden'
              }}
              >
              {task.responsible?.map((person, index) => (
              <Chip
              key={index}
              label={`${person.last_name} ${person.first_name}`}
              size="small"
              />
              ))}
              </Box>
              </TableCell>
              {/* TYPES */}
              <TableCell sx={{ maxWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {task.task_type?.map((type, index) => (

              <Chip
              key={index}
              label={type.name}
              size="small"
              variant="outlined"
              />
              ))}
              </Box>
              </TableCell>
              {/* PRIORITY */}
              <TableCell>
              <Chip
              label={getPriorityLabel(task.priority)}
              color={getPriorityColor(task.priority)}
              size="small"
              />
              </TableCell>
              {/* DATES */}
              <TableCell>
              <Typography variant="caption" color="text.secondary">
              from {formatDate(task.start_date)}
              </Typography>
              {task.end_date && (
              <Typography variant="body2">
              to {formatDate(task.end_date)}
              </Typography>
              )}
              </TableCell>
              {/* COMPLETE */}
              <TableCell>
              <Box
              sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
              }}
              >
              <Typography variant="caption">
              {task.complete}%
              </Typography>
              <LinearProgress
              variant="determinate"
              value={task.complete}
              sx={{
              width: 60,
              height: 6,
              borderRadius: 3
              }}
              />
              </Box>
              </TableCell>
              {/* ACTIONS */}
              <TableCell
                align="center"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: 60,
                  minWidth: 60,
                  maxWidth: 60,
                  px: 0.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',   // 🔥 KLUCZOWE
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}
                >
                  <Tooltip title="Add Progress">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                        setTaskProgressModalOpen(true);
                      }}
                    >
                      <AddComment fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                        setTaskModalOpen(true);
                      }}
                    >
                      <Edit fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTaskToDelete(task);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              </TableRow>
            ))}
            </TableBody>
          </Table>
        <TablePagination
          component="div"
          count={metadata.total_count || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Tasks per page:"
        />
      </TableContainer>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{taskToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <TaskFormModal 
        open={taskModalOpen} 
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id || null}
        onSave={async () => {
          await refetch();
          await refetchTypes();
        }}
      />
      <TaskViewModal
        open={taskViewModalOpen}
        onClose={() => {
          setTaskViewModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
      />
      <TaskProgressModal
        open={taskProgressModalOpen}
        onClose={() => {
          setTaskProgressModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
        onSave={async () => {
          await refetch();
        }}
      />
    </Box>
  );
};

export default TasksList;
